import {
  Component,
  effect,
  ElementRef,
  inject,
  signal,
  ViewChild,
  WritableSignal,
} from "@angular/core";
import {
  OptionSwitchItem,
  OptionSwitchComponent,
} from "../../../shared/components/option-switch/option-switch.component";
import { RevenueChartLineService } from "../../../shared/services/revenue-line-chart.service";
import { GloGridComponent } from "../../../shared/components/grid/grid.component";
import { ColDef } from "ag-grid-community";
import { StatusTagInfo } from "../../../shared/components/tag/tag.component";
import { API_ENDPOINTS } from "../../../shared/services/api/api-endpoints";
import {
  cardsData,
  chartData,
  orderData,
  orders,
  orderStats,
} from "../../../shared/models/dashboard.model";
import { ApiService } from "../../../shared/services/api/api.service";
import { LoaderComponent } from "../../../shared/components/loader/loader.component";
import dayjs from "dayjs";
import { environment } from "../../../../environments/environment";
import { FormsModule } from "@angular/forms";
import { BusinessSalonContextService } from "../../../shared/services/api/business-salon-context.service";

@Component({
  selector: "business-sales-finances",
  standalone: true,
  imports: [
    OptionSwitchComponent,
    GloGridComponent,
    LoaderComponent,
    FormsModule,
  ],
  templateUrl: "./sales-finances.component.html",
  styleUrl: "./sales-finances.component.scss",
})
export class BusinessSalesFinancesComponent {
  assetUrl = environment.assetBaseUrl;

  private revenueChartService = inject(RevenueChartLineService);
  readonly salonContext = inject(BusinessSalonContextService);

  @ViewChild("revenueChartCanvas", { static: true })
  chartCanvas!: ElementRef<HTMLCanvasElement>;

  private analyticsLoading = false;
  private ordersLoading = false;

  get isLoading(): boolean {
    return this.analyticsLoading || this.ordersLoading;
  }

  cardsData: cardsData = {} as cardsData;
  ChartData: WritableSignal<chartData> = signal<chartData>({} as chartData);

  orders: orders[] = [];
  filteredOrders: orders[] = [];
  stats: orderStats = {} as orderStats;

  selectedPeriod = "yearly";
  selectedOrderType = "all";

  constructor(private apiServices: ApiService) {
    effect(() => {
      this.revenueChartService.updateChartData(
        this.selectedPeriod,
        this.ChartData(),
      );
    });

    effect(() => {
      const salons = this.salonContext.salons();
      this.salonContext.selectedSalonIds();

      if (!salons.length) {
        return;
      }

      this.loadAnalyticsData();
      this.loadOrdersData();
    });
  }

  timePeriods: OptionSwitchItem[] = [
    { label: "Yearly", value: "yearly" },
    { label: "Quarterly", value: "quarterly" },
    { label: "Monthly", value: "monthly" },
    { label: "Weekly", value: "weekly" },
    { label: "Daily", value: "daily" },
  ];

  orderTypes: OptionSwitchItem[] = [
    { label: "All Orders", value: "all" },
    { label: "Paid", value: "paid" },
    { label: "Unpaid", value: "unpaid" },
    { label: "Refunded", value: "refunded" },
    { label: "Confirmed", value: "confirmed" },
    { label: "Pending", value: "pending" },
    { label: "Completed", value: "completed" },
    { label: "Cancelled", value: "cancelled" },
    { label: "No Show", value: "no_show" },
  ];

  readonly statusStyles = StatusTagInfo;

  colDefs: ColDef[] = [
    {
      headerName: "",
      field: "checkbox",
      width: 50,
      checkboxSelection: true,
      onCellValueChanged: (params) => {
        if (params.newValue === true) {
          this.openOrderDetails(params.data);
        }
      },
    },
    {
      headerName: "Invoice",
      field: "invoice",
      flex: 1,
    },
    {
      headerName: "Date",
      field: "date",
      flex: 1,
      cellRenderer: (params: any) => {
        const formattedDate = this.formatDateString(
          params.data?.date ||
            params.data?.start_time ||
            params.data?.created_at,
        );

        return `
          <span>
            ${formattedDate}
          </span>
        `;
      },
    },
    {
      headerName: "Appointment",
      field: "status",
      flex: 1,

      cellRenderer: (params: any) => {
        const state = this.getAppointmentStatus(params.data) || "default";

        const cssClass = this.getAppointmentStatusClass(state);

        return `
      <span class="status-pill ${cssClass}">
        ${this.formatStatus(state)}
      </span>
    `;
      },
    },
    {
      headerName: "Payment",
      field: "payment_status",
      flex: 1,

      cellRenderer: (params: any) => {
        const state = this.getPaymentStatus(params.data) || "default";

        const cssClass = this.getPaymentStatusClass(state);

        return `
      <span class="status-pill ${cssClass}">
        ${this.formatStatus(state)}
      </span>
    `;
      },
    },
    {
      headerName: "Customer",
      field: "customer_name",
      flex: 2,
      cellRenderer: (params: any) => {
        const customerName =
          params.data?.customer_name ||
          params.data?.customer?.full_name ||
          params.data?.customer?.name ||
          "Unknown Customer";

        const customerEmail =
          params.data?.customer_email ||
          params.data?.customer?.email ||
          "No email";

        const initials = customerName
          .split(" ")
          .filter(Boolean)
          .slice(0, 2)
          .map((name: string) => name[0])
          .join("")
          .toUpperCase();

        const avatar = params.data?.avatar
          ? `<img src="${params.data.avatar}" alt="" class="w-8 h-8 rounded-full mr-3 inline-block align-middle" />`
          : `<div class="w-8 h-8 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center mr-3 inline-block align-middle font-semibold">${initials}</div>`;

        return `
          <div class="flex items-center">
            ${avatar}
            <div class="flex flex-col">
              <span class="text-gray-900 text-sm font-medium">${customerName}</span>
              <span class="text-gray-500 text-xs">${customerEmail}</span>
            </div>
          </div>
        `;
      },
    },
  ];

  ngOnInit(): void {
    /*
     * Data loading is handled by the salon-context effect.
     */
  }

  ngAfterViewInit(): void {
    this.revenueChartService.initChart(this.chartCanvas.nativeElement);
  }

  get paidOrdersCount(): number {
    return this.orders.filter(
      (order: any) => this.getPaymentStatus(order) === "paid",
    ).length;
  }

  get averageOrderValue(): number {
    if (!this.orders.length) {
      return 0;
    }

    const total = this.orders.reduce((sum: number, order: any) => {
      const value = Number(
        order?.total ??
          order?.total_price ??
          order?.amount ??
          order?.order_total ??
          0,
      );

      return sum + (Number.isFinite(value) ? value : 0);
    }, 0);

    return total / this.orders.length;
  }

  loadAnalyticsData(): void {
    this.analyticsLoading = true;

    const url = this.salonContext.withSalonScope(
      API_ENDPOINTS.owner.analytics.overview,
    );

    this.apiServices.request<any>(url, "GET").subscribe({
      next: (response: any) => {
        this.cardsData = response?.cards || ({} as cardsData);

        this.ChartData.set(response?.charts || ({} as chartData));

        this.revenueChartService.updateChartData(
          this.selectedPeriod,
          response?.charts || ({} as chartData),
        );

        this.analyticsLoading = false;
      },

      error: (error: any) => {
        console.error("Error loading analytics data:", error);

        this.cardsData = {} as cardsData;
        this.ChartData.set({} as chartData);

        this.analyticsLoading = false;
      },
    });
  }

  loadOrdersData(): void {
    this.ordersLoading = true;

    const url = this.salonContext.withSalonScope(
      API_ENDPOINTS.owner.analytics.orders,
    );

    this.apiServices.request<orderData>(url, "GET").subscribe({
      next: (response: any) => {
        const rawOrders = response?.data?.orders ?? response?.orders ?? [];

        this.orders = Array.isArray(rawOrders) ? rawOrders : [];

        this.stats =
          response?.data?.stats ?? response?.stats ?? ({} as orderStats);

        this.applyOrderFilter();

        this.ordersLoading = false;
      },

      error: (error: any) => {
        console.error("Error loading orders data:", error);

        this.orders = [];
        this.filteredOrders = [];
        this.stats = {} as orderStats;

        this.ordersLoading = false;
      },
    });
  }

  openOrderDetails(order: orders): void {
    console.log("Open order details for:", order);
  }

  onPeriodChange(selectedPeriod: string): void {
    this.selectedPeriod = selectedPeriod;

    this.revenueChartService.updateChartData(selectedPeriod, this.ChartData());
  }

  onMobilePeriodChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;

    this.onPeriodChange(value);
  }

  getAppointmentStatusClass(status: string): string {
    switch ((status || "").trim().toLowerCase()) {
      case "completed":
        return "status-pill--completed";

      case "confirmed":
        return "status-pill--confirmed";

      case "pending":
        return "status-pill--pending";

      case "cancelled":
      case "canceled":
        return "status-pill--cancelled";

      case "no_show":
        return "status-pill--no-show";

      default:
        return "status-pill--default";
    }
  }

  getPaymentStatusClass(status: string): string {
    switch ((status || "").trim().toLowerCase()) {
      case "paid":
        return "payment-pill--paid";

      case "unpaid":
      case "pending":
        return "payment-pill--unpaid";

      case "partially_paid":
        return "payment-pill--partially-paid";

      case "refunded":
        return "payment-pill--refunded";

      case "failed":
        return "payment-pill--failed";

      default:
        return "payment-pill--default";
    }
  }

  onOrdersPeriodChange(selectedPeriod: string): void {
    this.selectedOrderType = (selectedPeriod || "all").trim().toLowerCase();

    this.applyOrderFilter();
  }

  private applyOrderFilter(): void {
    const selected = (this.selectedOrderType || "all").trim().toLowerCase();

    if (selected === "all") {
      this.filteredOrders = [...this.orders];

      return;
    }

    this.filteredOrders = this.orders.filter((order: any) => {
      const paymentStatus = this.getPaymentStatus(order);

      const appointmentStatus = this.getAppointmentStatus(order);

      switch (selected) {
        case "paid":
          return paymentStatus === "paid";

        case "unpaid":
          return (
            !paymentStatus ||
            paymentStatus === "unpaid" ||
            paymentStatus === "pending" ||
            paymentStatus === "failed" ||
            paymentStatus === "partially_paid"
          );

        case "refunded":
          return paymentStatus === "refunded";

        case "confirmed":
          return appointmentStatus === "confirmed";

        case "pending":
          return appointmentStatus === "pending";

        case "completed":
          return appointmentStatus === "completed";

        case "cancelled":
          return appointmentStatus === "cancelled";

        case "no_show":
          return appointmentStatus === "no_show";

        default:
          return true;
      }
    });
  }

  private getPaymentStatus(order: any): string {
    return (
      order?.payment_status ??
      order?.paymentStatus ??
      order?.payment?.status ??
      order?.appointment?.payment_status ??
      order?.appointment?.paymentStatus ??
      ""
    )
      .toString()
      .trim()
      .toLowerCase();
  }

  private getAppointmentStatus(order: any): string {
    const value =
      order?.status ??
      order?.appointment_status ??
      order?.appointmentStatus ??
      order?.appointment?.status ??
      "";

    const normalized = value.toString().trim().toLowerCase();

    /*
     * Normalize legacy aliases if any old
     * order responses still use them.
     */
    if (normalized === "booked") {
      return "confirmed";
    }

    if (normalized === "complete") {
      return "completed";
    }

    return normalized;
  }

  formatStatus(status: string): string {
    const normalized = (status || "").trim().toLowerCase();

    switch (normalized) {
      case "pending":
        return "Pending";

      case "confirmed":
        return "Confirmed";

      case "cancelled":
        return "Cancelled";

      case "completed":
        return "Completed";

      case "no_show":
        return "No Show";

      case "paid":
        return "Paid";

      case "failed":
        return "Failed";

      case "refunded":
        return "Refunded";

      case "default":
      case "":
        return "N/A";

      default:
        return normalized.charAt(0).toUpperCase() + normalized.slice(1);
    }
  }

  downloadReport(): void {
    const labels = this.revenueChartService.getChartLabels(
      this.selectedPeriod,
      this.ChartData(),
    );

    const data = this.revenueChartService.getChartValues(
      this.selectedPeriod,
      this.ChartData(),
    );

    const BOM = "\uFEFF";

    let csvContent = "Time Period,Revenue ($)\n";

    labels.forEach((label, index) => {
      const revenue = data[index] ?? 0;

      csvContent += `${label},${revenue}\n`;
    });

    const blob = new Blob([BOM, csvContent], {
      type: "text/csv",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");

    const filename = `revenue-report-${this.selectedPeriod}.csv`;

    link.href = url;
    link.download = filename;

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  }

  formatDateString(dateString: string, format?: string): string {
    if (!dateString || !dayjs(dateString).isValid()) {
      return "";
    }

    return dayjs(dateString).format(format || "MMM D, YYYY");
  }

  toMoney(value: any): string {
    const amount = Number(value);

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(Number.isFinite(amount) ? amount : 0);
  }
}
